/**
 * Shared, importable user-detail read helpers.
 *
 * These extract the LIVE per-user reads that AdminDashboard.openUserDetail
 * performed inline (the url-subcollection fetch, classifyUrlItem grouping, and
 * per-test testingSessions fetch), plus a subscription-derived plan read, so
 * both the Figmafolio AdminDashboard and the Aero /users page can share the
 * EXACT same rules-permitted read paths. Do not fork these: the session fetch
 * below is the precise shape the Firestore rules allow an admin to read.
 *
 * `db` is a firebase compat firestore instance (firebase.firestore()).
 */
import { classifyUrlItem } from './brandInference';
import { getPlanKeyFromPriceId } from '../../utils/pricingUtils';

/**
 * Derive a display plan + raw status from a subscription doc, mirroring the
 * server-side derivePlanFromSubscription in functions/planMapping.js so the
 * admin surfaces never disagree with the nightly rollup:
 *   trialing            -> "Trial"
 *   active | past_due    -> priceId lookup -> "Basic" | "Solo" | "Pro" (else "Free")
 *   anything else / none -> "Free"
 *
 * @param {object|null} subData - the latest subscription doc data, or null.
 * @returns {{ plan: string, planStatus: string, priceId: string|null }}
 */
export function derivePlanFromSubData(subData) {
  if (!subData) return { plan: 'Free', planStatus: 'none', priceId: null };

  const status = subData.status;
  const priceId =
    subData.items?.[0]?.price?.id || subData.items?.[0]?.plan?.id || null;

  if (status === 'trialing') {
    return { plan: 'Trial', planStatus: status, priceId };
  }
  if (status === 'active' || status === 'past_due') {
    const planKey = getPlanKeyFromPriceId(priceId);
    if (planKey === 'pro') return { plan: 'Pro', planStatus: status, priceId };
    if (planKey === 'solo') return { plan: 'Solo', planStatus: status, priceId };
    if (planKey === 'basic') return { plan: 'Basic', planStatus: status, priceId };
    return { plan: 'Free', planStatus: status, priceId };
  }
  return { plan: 'Free', planStatus: status || 'none', priceId };
}

/**
 * Read the user's latest subscription doc and derive their real (subscription)
 * plan. The `plan` here is the true paid tier; a plan OVERRIDE (planOverride on
 * the user doc, set by an admin) is layered on top by the caller so the two can
 * be shown distinctly. Never reads user.subscriptionStatus (a stale signup
 * stamp).
 *
 * @param {object} db - firebase.firestore() instance
 * @param {string} uid
 * @returns {Promise<{ plan: string, planStatus: string, priceId: string|null }>}
 */
export async function readUserPlan(db, uid) {
  const snap = await db
    .collection('user').doc(uid)
    .collection('subscriptions')
    .orderBy('created', 'desc')
    .limit(1)
    .get();
  const subData = snap.empty ? null : snap.docs[0].data();
  return derivePlanFromSubData(subData);
}

/**
 * Live-fetch a user's owned items across BOTH brands and group them exactly as
 * openUserDetail did: one url-subcollection read, classifyUrlItem grouping into
 * html / figma / tests, and a per-test testingSessions read whose docs are
 * reduced to { id, startedAt, hasMedia } for the session-viewer deep links.
 *
 * @param {object} db - firebase.firestore() instance
 * @param {string} uid
 * @returns {Promise<{ html: object[], figma: object[], tests: object[] }>}
 */
export async function readUserItems(db, uid) {
  const urlSnap = await db
    .collection('user').doc(uid).collection('url').get();
  const docs = urlSnap.docs.map((d) => ({ key: d.id, userId: uid, ...d.data() }));
  const html = [];
  const figma = [];
  const tests = [];
  // Classify with the SAME rule as the rollup (classifyUrlItem) so this never
  // disagrees with the list / BAN counts.
  docs.forEach((d) => {
    const kind = classifyUrlItem(d);
    if (kind === 'test') tests.push(d);
    else if (kind === 'html') html.push(d);
    else figma.push(d);
  });
  // Pull sessions for each test (for the session-viewer deep links). This is
  // the exact rules-permitted read (owner-or-admin) — do not change its shape.
  await Promise.all(tests.map(async (t) => {
    try {
      const ss = await db
        .collection('user').doc(uid)
        .collection('url').doc(t.key)
        .collection('testingSessions').get();
      t.sessions = ss.docs.map((s) => {
        const sd = s.data();
        return {
          id: s.id,
          startedAt: sd.startedAt || sd.createdAt || null,
          hasMedia: !!(sd.videoUrl || sd.audioUrl),
        };
      });
    } catch (err) {
      t.sessions = [];
    }
  }));
  return { html, figma, tests };
}
